import { Button } from '#app/components/Buttons/Button.js';
import { ButtonSize, ButtonVariant } from '#app/components/Buttons/types.js';
import { Link } from '#app/components/Link/index.js';
import {
  ContentRow,
  DesktopCloseButton,
  MobileCloseButton,
} from './Banners.styles.js';
import { BannerWrapper } from './BannerWrapper.js';
import { type BaseBannerProps } from './types.js';
import {
  ChevronRightIcon,
  CloseIcon,
  ExternalLinkAltIcon,
} from '@contra/icons/icons';
import { css, styled } from 'styled-components';

type LinkBannerProps = BaseBannerProps & {
  readonly buttonDisabled?: boolean;
  readonly buttonSize?: ButtonSize;
  readonly buttonVariant?: ButtonVariant;
  readonly hideButtonIcon?: boolean;
  readonly href?: string;
  readonly linkLabel: string;
  readonly onClick?: () => void;
  readonly useButton?: boolean;
};

const StyledTextLink = styled(Link)`
  ${({ theme }) => css`
    display: inline;

    font-size: 14px;
    line-height: 1.4;
    letter-spacing: 0.15px;
    font-weight: 600;
    text-align: left;

    text-decoration: underline 1px transparent;
    text-underline-offset: 8px;
    transition: text-decoration-color 100ms ease-out;
    cursor: pointer;

    :hover {
      text-decoration-color: ${theme.colorsV2.black};
    }

    & > svg {
      width: 16px;
      height: 16px;
      position: relative;
      top: 4px;
    }

    ${theme.mediaQueries.sm} {
      font-size: 16px;
      line-height: 1.5;

      & > svg {
        width: 24px;
        height: 24px;
        top: 6px;
      }
    }

    ${theme.mediaQueries.md} {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      white-space: nowrap;

      & > svg {
        top: unset;
      }
    }
  `}
`;

const ButtonLayout = styled.div`
  margin-left: auto;
`;

export const LinkBanner = ({
  buttonDisabled,
  buttonSize,
  buttonVariant,
  centerContent,
  hideButtonIcon,
  href,
  isDismissible,
  linkLabel,
  onClick,
  onClose,
  ...props
}: LinkBannerProps) => {
  return (
    <BannerWrapper
      {...props}
      centerContent={centerContent}
    >
      <ContentRow centerContent={centerContent}>
        {props.useButton ? (
          <ButtonLayout>
            <Button
              disabled={buttonDisabled ?? false}
              icon={hideButtonIcon ? undefined : ExternalLinkAltIcon}
              onClick={onClick}
              size={buttonSize ?? 'medium'}
              variant={buttonVariant ?? 'V2TertiaryOutline'}
              {...(href
                ? {
                    as: 'a',
                    href,
                    rel: 'noopener noreferrer',
                    target: '_blank',
                  }
                : {})}
            >
              {linkLabel}
            </Button>
          </ButtonLayout>
        ) : (
          <StyledTextLink
            as={href ? Link : 'span'}
            onClick={onClick}
            role={href ? 'link' : 'button'}
            to={href ?? ''}
          >
            {linkLabel}
            <ChevronRightIcon />
          </StyledTextLink>
        )}
        {isDismissible ? (
          <MobileCloseButton onClick={onClose}>Close</MobileCloseButton>
        ) : null}
      </ContentRow>
      {isDismissible ? (
        <DesktopCloseButton
          aria-label="Dismiss Tip"
          onClick={onClose}
        >
          <CloseIcon />
        </DesktopCloseButton>
      ) : null}
    </BannerWrapper>
  );
};
